/**
 * <ffp-bouquet-builder>
 *
 * Live volume-tier pricing for custom rose bouquets, cart drawer integration,
 * and line item properties for checkout (including _price_inr for Cart Transform).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { buildWhatsAppUrl } from '@theme/ffp-whatsapp-order';
import { assertPinGate, getPinGateState } from '@theme/ffp-pin-gate';
import { syncBuilderPricingUi } from '@theme/ffp-pricing-modal';

class FfpBouquetBuilder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-ffp-builder-form]');
    this.roseCountField = this.querySelector('[data-ffp-rose-count]');
    this.customCountField = this.querySelector('[data-ffp-custom-count]');
    this.customCountWrapper = this.querySelector('[data-ffp-custom-count-field]');
    this.roseColourField = this.querySelector('[data-ffp-rose-colour]');
    this.priceEl = this.querySelector('[data-ffp-price]');
    this.breakdownEl = this.querySelector('[data-ffp-breakdown]');
    this.addToCartButton = this.querySelector('[data-ffp-add-to-cart]');
    this.whatsappCta = this.querySelector('[data-ffp-whatsapp-cta]');
    this.statusEl = this.querySelector('[data-ffp-status]');

    this.volumeThreshold = Number(this.dataset.volumeThreshold) || 250;
    this.rates = {
      red: {
        upTo: Number(this.dataset.redRateUpTo250) || 30,
        above: Number(this.dataset.redRateAbove250) || 10,
      },
      premium: {
        upTo: Number(this.dataset.premiumRateUpTo250) || 50,
        above: Number(this.dataset.premiumRateAbove250) || 15,
      },
    };
    this.addOnPrices = {
      chocolates: Number(this.dataset.priceChocolates) || 300,
      led: Number(this.dataset.priceLed) || 250,
      moneyBouquet: Number(this.dataset.priceMoneyBouquet) || 500,
    };
    this.variantId = this.dataset.variantId;
    this.whatsappNumber = this.dataset.whatsappNumber || '';
    this.whatsappMessageLink = this.dataset.whatsappMessageLink || '';

    this.form?.addEventListener('input', () => this.update());
    this.roseCountField?.addEventListener('change', () => {
      const isCustom = this.roseCountField.value === 'custom';
      this.customCountWrapper?.toggleAttribute('hidden', !isCustom);
      this.update();
    });
    this.addToCartButton?.addEventListener('click', () => this.addToCart());
    this.whatsappCta?.addEventListener('click', (event) => this.#handleWhatsAppClick(event));

    this.querySelector('[data-ffp-builder-expand]')?.addEventListener('click', () => {
      this.expandBuilder();
    });

    if (window.location.hash === '#customize-bouquet') {
      this.expandBuilder();
    }

    this.update();
    this.applyUrlAddons();
  }

  applyUrlAddons() {
    const addon = new URLSearchParams(window.location.search).get('addon');
    if (!addon || !this.form) return;
    const input = this.form.querySelector(`[data-ffp-addon="${addon}"]`);
    if (input instanceof HTMLInputElement) {
      input.checked = true;
      this.update();
    }
  }

  expandBuilder() {
    const formArea = this.querySelector('#ffp-builder-form-area');
    formArea?.classList.remove('ffp-builder__form-area--collapsed');
    this.querySelector('[data-ffp-builder-teaser]')?.setAttribute('hidden', '');
    formArea?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getRoseCount() {
    if (this.roseCountField?.value === 'custom') {
      return Math.max(Number(this.customCountField?.value) || 0, 0);
    }
    return Number(this.roseCountField?.value) || 0;
  }

  getRate() {
    const selectedOption = this.roseColourField?.selectedOptions?.[0];
    const tier = selectedOption?.dataset.tier === 'red' ? 'red' : 'premium';
    const roseCount = this.getRoseCount();
    const rateSet = this.rates[tier];
    const rate = roseCount > this.volumeThreshold ? rateSet.above : rateSet.upTo;
    const tierLabel =
      roseCount > this.volumeThreshold
        ? `volume (${this.volumeThreshold + 1}+ stems)`
        : `standard (up to ${this.volumeThreshold} stems)`;

    return { tier, rate, tierLabel };
  }

  getAddOns() {
    return Array.from(this.querySelectorAll('[data-ffp-addon]'))
      .filter((input) => input.checked)
      .map((input) => input.dataset.ffpAddon);
  }

  getSelections() {
    const roseCount = this.getRoseCount();
    const { tier, rate, tierLabel } = this.getRate();
    const addOns = this.getAddOns();

    const addOnLabels = {
      chocolates: ['Chocolates', this.addOnPrices.chocolates],
      led: ['LED Lights', this.addOnPrices.led],
      moneyBouquet: ['Money Bouquet', this.addOnPrices.moneyBouquet],
    };

    const roseSubtotal = roseCount * rate;
    const addOnLines = addOns.map((key) => ({
      label: addOnLabels[key][0],
      amount: addOnLabels[key][1],
    }));
    const addOnTotal = addOnLines.reduce((sum, line) => sum + line.amount, 0);

    return {
      roseCount,
      tier,
      rate,
      tierLabel,
      roseSubtotal,
      addOnLines,
      addOnTotal,
      total: roseSubtotal + addOnTotal,
      wrapping: this.querySelector('[name="wrapping_style"]')?.value,
      ribbon: this.querySelector('[name="ribbon"]')?.value,
      colourLabel: this.roseColourField?.value,
      cardMessage: this.querySelector('[name="card_message"]')?.value?.trim(),
    };
  }

  formatCurrency(amount) {
    return `₹${amount.toLocaleString('en-IN')}`;
  }

  update() {
    const selections = this.getSelections();

    if (this.priceEl) {
      this.priceEl.textContent = this.formatCurrency(selections.total);
    }

    if (this.breakdownEl) {
      const rows = [
        `<div class="ffp-builder__row"><span>${selections.roseCount} roses &times; ${this.formatCurrency(selections.rate)} <small>(${selections.tierLabel})</small></span><span>${this.formatCurrency(selections.roseSubtotal)}</span></div>`,
        ...selections.addOnLines.map(
          (line) =>
            `<div class="ffp-builder__row"><span>${line.label}</span><span>${this.formatCurrency(line.amount)}</span></div>`
        ),
      ];
      this.breakdownEl.innerHTML = rows.join('');
    }

    syncBuilderPricingUi(this);
  }

  /** @param {MouseEvent} event */
  #handleWhatsAppClick(event) {
    event.preventDefault();
    if (!assertPinGate()) return;

    const selections = this.getSelections();
    if (selections.roseCount <= 0) {
      this.setStatus('Please choose a valid number of roses.', 'error');
      return;
    }

    const url = this.buildWhatsAppLink(selections);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  buildWhatsAppLink(selections) {
    const lines = [
      'Hi Fresh FlowerPoint, I would like to order a custom bouquet:',
      `- Roses: ${selections.roseCount} (${selections.colourLabel})`,
      `- Wrapping: ${selections.wrapping}`,
      `- Ribbon: ${selections.ribbon}`,
      selections.addOnLines.length ? `- Add-ons: ${selections.addOnLines.map((l) => l.label).join(', ')}` : null,
      selections.cardMessage ? `- Card message: ${selections.cardMessage}` : null,
      `- Estimated total: ${this.formatCurrency(selections.total)}`,
      '- Delivery: Delhi NCR (advance order · earliest next day)',
    ].filter(Boolean);

    return buildWhatsAppUrl(lines.join('\n'), {
      whatsappNumber: this.whatsappNumber,
      whatsappMessageLink: this.whatsappMessageLink,
    });
  }

  setStatus(message, state) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.dataset.state = state;
    this.statusEl.hidden = !message;
  }

  /** @returns {Promise<any>} */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds;
  }

  async addToCart() {
    if (!this.variantId) {
      this.setStatus('This builder is not connected to a product yet. Please use "Order via WhatsApp" instead.', 'error');
      return;
    }

    if (!assertPinGate()) {
      this.setStatus(getPinGateState().message, 'error');
      return;
    }

    const selections = this.getSelections();
    if (selections.roseCount <= 0) {
      this.setStatus('Please choose a valid number of roses.', 'error');
      return;
    }

    this.addToCartButton.disabled = true;
    this.setStatus('Adding to cart…', '');

    const properties = {
      'Rose Count': String(selections.roseCount),
      'Rose Colour': selections.colourLabel,
      'Wrapping Style': selections.wrapping,
      Ribbon: selections.ribbon,
      'Add-ons': selections.addOnLines.map((l) => l.label).join(', ') || 'None',
      'Estimated Total': this.formatCurrency(selections.total),
      _price_inr: selections.total.toFixed(2),
    };
    if (selections.cardMessage) {
      properties['Card Message'] = selections.cardMessage;
    }

    const sectionIds = this.#getCartSectionIds();
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: this.variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(this.variantId), quantity: 1, properties }],
          sections: sectionIds.join(','),
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || 'Could not add this bouquet to your cart.');
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ffp-bouquet-builder',
          sourceId: this.id || 'ffp-bouquet-builder',
          itemCount: 1,
          sections: payload.sections,
          didError: false,
        },
      });

      this.setStatus('Added to your bag!', 'success');
      this.addToCartButton.disabled = false;
    } catch (error) {
      this.dispatchEvent(
        new CartErrorEvent({
          error: error.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );

      try {
        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'ffp-bouquet-builder' },
        });
      } catch {
        deferredEventPromise.reject(error);
      }

      this.setStatus(error.message || 'Something went wrong. Please try again or order via WhatsApp.', 'error');
      this.addToCartButton.disabled = false;
    }
  }
}

if (!customElements.get('ffp-bouquet-builder')) {
  customElements.define('ffp-bouquet-builder', FfpBouquetBuilder);
}
